import { LayoutTransform, Layer_t, hasSlipSheet, Layout_t } from 'blackbox';
import { ChangeEvent } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { wrapChangeEventNumber } from '../../shared/shared';
import { ManualStackProps } from './stackTypes';

export default function ManualStack({
	palletIndex,
	allPallets,
	allSlipSheets,
	addLayer,
	setLayoutOnLayer,
	setSlipSheetPresentOnLayer,
	setSlipSheetTypeOnLayer,
	removeLayer,
	editingStack,
	setTransformOnLayer,
	triggerSelect,
	slipsheetEnabled,
}: ManualStackProps) {
	const pallet = allPallets[palletIndex];
	const { Layouts } = pallet;
	const stack = editingStack.layers;

	const handleLayoutSelection =
		(layerIndex: number) => (selectedLayoutIndex: number) => {
			setLayoutOnLayer(layerIndex, selectedLayoutIndex);
		};

	function setLayerTransformIfChanged(
		layerIndex: number,
		selectedTransform: LayoutTransform.TransformType
	) {
		const currentTransform = stack[layerIndex].transform;
		const newTransform =
			selectedTransform !== currentTransform
				? selectedTransform
				: LayoutTransform.TransformType.Identity;

		setTransformOnLayer(layerIndex, newTransform);
	}

	const handleTransformSelection =
		(layerIndex: number) => (transformId: LayoutTransform.TransformType) => {
			setLayerTransformIfChanged(layerIndex, transformId);
		};

	const triggerLayoutSelectionDialog =
		(layerIndex: number) => (event: React.SyntheticEvent) => {
			if (!triggerSelect) return;
			event.preventDefault();
			triggerSelect(
				'Select Layout',
				Layouts.map(layout => layout.name),
				handleLayoutSelection(layerIndex),
				stack[layerIndex].index
			);
		};

	const triggerTransformSelectionDialog =
		(layerIndex: number) => (event: React.SyntheticEvent) => {
			if (!triggerSelect) return;
			event.preventDefault();
			triggerSelect(
				'Select Layer Behavior',
				LayoutTransform.TransformTypeNames,
				handleTransformSelection(layerIndex),
				stack[layerIndex].transform
			);
		};

	function SlipSheetTypeSelector({ layerIndex }: { layerIndex: number }) {
		return (
			<>
				<span className="double-padding-left margin-right"> Type </span>
				{!!editingStack.slipSheet && (
					<select
						value={editingStack.slipSheet.ids[layerIndex]}
						onChange={(event: ChangeEvent<HTMLSelectElement>) =>
							setSlipSheetTypeOnLayer(layerIndex, event.target.value)
						}
					>
						{allSlipSheets.map((ss, idx) => (
							<option value={ss.id} key={idx}>
								{ss.name}
							</option>
						))}
					</select>
				)}
			</>
		);
	}

	return (
		<div className="full-width-minus-pad StackConfigurator">
			<div className="flex-end full-width double-margin-top">
				<div
					className="flex-center width-30 hover-underline cursor-pointer blue-inner-border rounded padding"
					onClick={addLayer}
				>
					<span className="padding-left"> Add Layer </span>
				</div>
			</div>

			{[...stack].reverse().map((layer: Layer_t, reversedIndex: number) => {
				const layerIndex = stack.length - (reversedIndex + 1);
				const hasSlipSheetOnLayer = hasSlipSheet(
					layerIndex,
					stack.length,
					editingStack.slipSheet
				);
				const hasSlipSheetOnTopLayer = hasSlipSheet(
					stack.length,
					stack.length,
					editingStack.slipSheet
				);

				return (
					<Fragment key={reversedIndex}>
						{reversedIndex === 0 && slipsheetEnabled && (
							<div className="flex-row align-center full-width padding-top double-padding-bottom double-padding-top">
								<div className="padding" />
								<input
									type="checkbox"
									className="padding"
									onChange={() =>
										setSlipSheetPresentOnLayer(stack.length, !hasSlipSheetOnTopLayer)
									}
									checked={hasSlipSheetOnTopLayer}
									style={{ height: '20px', width: '20px' }}
								/>
								<span className="color-blue double-margin-right">Slip Sheet</span>
								{hasSlipSheetOnTopLayer && editingStack.slipSheet && (
									<SlipSheetTypeSelector layerIndex={stack.length} />
								)}
							</div>
						)}

						<div className="full-width flex-around background-white dark-border padding-top padding-bottom rounded align-center">
							<div className="flex-center">
								<span>{`Layer ${layerIndex + 1}`}</span>
							</div>

							<div className="flex-center">
								<select
									value={layer.index}
									onChange={wrapChangeEventNumber(handleLayoutSelection(layerIndex))}
									onMouseDown={triggerLayoutSelectionDialog(layerIndex)}
								>
									{Layouts.map((layout: Layout_t, j: number) => (
										<option value={j} key={j}>
											{layout.name}
										</option>
									))}
								</select>
							</div>

							<div className="flex-center">
								<select
									value={layer.transform}
									onChange={wrapChangeEventNumber(handleTransformSelection(layerIndex))}
									onMouseDown={triggerTransformSelectionDialog(layerIndex)}
								>
									{LayoutTransform.TransformTypeNames.map(
										(transformName: string, transformIndex: number) => (
											<option key={transformIndex} value={transformIndex}>
												{transformName}
											</option>
										)
									)}
								</select>
							</div>

							<div
								className="flex-center cursor-pointer"
								onClick={() => removeLayer(layerIndex)}
							>
								<span className="icon-delete color-red hover-underline"></span>
							</div>
						</div>

						{slipsheetEnabled && (
							<div className="flex-row align-center full-width double-padding-bottom double-padding-top">
								<div className="padding" />
								<input
									type="checkbox"
									className="padding"
									onChange={() =>
										setSlipSheetPresentOnLayer(layerIndex, !hasSlipSheetOnLayer)
									}
									checked={hasSlipSheetOnLayer}
									style={{ height: '20px', width: '20px' }}
								/>
								<span className="color-blue double-margin-right">Slip Sheet</span>
								{hasSlipSheetOnLayer && (
									<SlipSheetTypeSelector layerIndex={layerIndex} />
								)}
							</div>
						)}
					</Fragment>
				);
			})}
		</div>
	);
}
